import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { OrgLoginDialog } from '@/components/public/org/OrgLoginDialog';
import { OrgRegisterDialog } from '@/components/public/org/OrgRegisterDialog';
import type { PublicOrganizationProfile } from '@/services/publicOrg';
import { useOrgRegisterDialogOptional } from '@/contexts/OrgRegisterDialogContext';

export type OpenOrgLoginOptions = {
  message?: string;
  prefillEmail?: string;
};

type OrgLoginDialogContextValue = {
  openOrgLogin: (returnUrl?: string, options?: OpenOrgLoginOptions) => void;
  closeOrgLogin: () => void;
};

const OrgLoginDialogContext = createContext<OrgLoginDialogContextValue | null>(null);

interface OrgLoginDialogProviderProps {
  profile: PublicOrganizationProfile;
  defaultReturnUrl: string;
  children: React.ReactNode;
}

export function OrgLoginDialogProvider({
  profile,
  defaultReturnUrl,
  children,
}: OrgLoginDialogProviderProps) {
  const [open, setOpen] = useState(false);
  const [returnUrl, setReturnUrl] = useState(defaultReturnUrl);
  const [message, setMessage] = useState<string | undefined>();
  const [prefillEmail, setPrefillEmail] = useState<string | undefined>();

  const openOrgLogin = useCallback(
    (returnUrlOverride?: string, options?: OpenOrgLoginOptions) => {
      setReturnUrl(returnUrlOverride ?? defaultReturnUrl);
      setMessage(options?.message);
      setPrefillEmail(options?.prefillEmail);
      setOpen(true);
    },
    [defaultReturnUrl]
  );

  const closeOrgLogin = useCallback(() => {
    setOpen(false);
    setReturnUrl(defaultReturnUrl);
  }, [defaultReturnUrl]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        setReturnUrl(defaultReturnUrl);
      }
    },
    [defaultReturnUrl]
  );

  const value = useMemo(
    () => ({ openOrgLogin, closeOrgLogin }),
    [openOrgLogin, closeOrgLogin]
  );

  return (
    <OrgLoginDialogContext.Provider value={value}>
      {children}
      <OrgLoginDialog
        profile={profile}
        open={open}
        onOpenChange={handleOpenChange}
        returnUrl={returnUrl}
        message={message}
        prefillEmail={prefillEmail}
      />
      <OrgRegisterDialogBridge profile={profile} />
    </OrgLoginDialogContext.Provider>
  );
}

function OrgRegisterDialogBridge({ profile }: { profile: PublicOrganizationProfile }) {
  const orgRegister = useOrgRegisterDialogOptional();
  if (!orgRegister) return null;

  return (
    <OrgRegisterDialog
      profile={profile}
      open={orgRegister.dialog.open}
      onOpenChange={orgRegister.dialog.onOpenChange}
      returnUrl={orgRegister.dialog.returnUrl}
      mode={orgRegister.dialog.mode}
    />
  );
}

export function useOrgLoginDialog(): OrgLoginDialogContextValue {
  const ctx = useContext(OrgLoginDialogContext);
  if (!ctx) {
    throw new Error('useOrgLoginDialog must be used within OrgLoginDialogProvider');
  }
  return ctx;
}

export function useOrgLoginDialogOptional(): OrgLoginDialogContextValue | null {
  return useContext(OrgLoginDialogContext);
}
